/**
 * CodeMirror 6 — Semantic YAML 자동완성
 *
 * 컨텍스트를 분석하여 적절한 완성 항목을 제공합니다:
 *   - 루트 키 (version, source, filter, joins, dimensions, measures)
 *   - 블록 속성 (name, expr, display_name, comment, synonyms, sql_hint, ...)
 *   - SQL 집계 함수, 스칼라 함수, 날짜 함수
 *   - 스니펫 (dimension/measure/join 블록 템플릿)
 *   - YAML 값 (version, join type 등)
 */
import { autocompletion } from '@codemirror/autocomplete'

// ── 완성 항목 정의 ──────────────────────────────────────────────────────────

// 1. 루트 레벨 키
const ROOT_KEYS = [
  { label: 'version', type: 'keyword', detail: '스키마 버전', apply: "version: '1.1'" },
  { label: 'source', type: 'keyword', detail: '팩트 테이블', apply: 'source: ' },
  { label: 'filter', type: 'keyword', detail: '기본 필터 (선택)', apply: 'filter: ""' },
  { label: 'joins', type: 'keyword', detail: '디멘전 테이블 조인', apply: 'joins:\n  - name: \n    source: \n    \'on\': ""' },
  { label: 'dimensions', type: 'keyword', detail: '디멘전 목록', apply: 'dimensions:\n  - name: \n    expr: \n    display_name: ""\n    comment: ""\n    synonyms: []\n    sql_hint: ""' },
  { label: 'measures', type: 'keyword', detail: '메저 목록', apply: 'measures:\n  - name: \n    expr: \n    display_name: ""\n    comment: ""\n    synonyms: []\n    sql_hint: ""' },
]

// 2. dimension / measure 블록 속성
const DIM_MEAS_PROPS = [
  { label: 'name', type: 'property', detail: '필드명 (snake_case)', boost: 10 },
  { label: 'expr', type: 'property', detail: 'SQL 표현식', boost: 9 },
  { label: 'display_name', type: 'property', detail: '한글 표시명', apply: 'display_name: ""', boost: 8 },
  { label: 'comment', type: 'property', detail: '설명', apply: 'comment: ""', boost: 7 },
  { label: 'synonyms', type: 'property', detail: '동의어 목록', apply: 'synonyms: []', boost: 6 },
  { label: 'sql_hint', type: 'property', detail: 'SQL 변환 힌트', apply: 'sql_hint: ""', boost: 5 },
]

// 3. join 블록 속성
const JOIN_PROPS = [
  { label: 'name', type: 'property', detail: '조인 별칭', boost: 10 },
  { label: 'source', type: 'property', detail: '디멘전 테이블 FQN', boost: 9 },
  { label: "'on'", type: 'property', detail: '조인 조건 (따옴표 필수)', apply: "'on': \"\"", boost: 8 },
  { label: 'type', type: 'property', detail: '조인 타입', boost: 7 },
  { label: 'using', type: 'property', detail: '조인 키 (on 대체)', boost: 6 },
]

// 4. 집계 함수 (measure expr용)
const AGG_FUNCTIONS = [
  { label: 'SUM', type: 'function', detail: '합계', apply: 'SUM(source.)', info: 'SUM(expr) — 숫자 합계' },
  { label: 'COUNT', type: 'function', detail: '카운트', apply: 'COUNT(source.)', info: 'COUNT(expr) — 행 수' },
  { label: 'COUNT_DISTINCT', type: 'function', detail: '고유 카운트', apply: 'COUNT(DISTINCT source.)', info: 'COUNT(DISTINCT expr) — 고유 값 수' },
  { label: 'AVG', type: 'function', detail: '평균', apply: 'AVG(source.)', info: 'AVG(expr) — 평균값' },
  { label: 'MIN', type: 'function', detail: '최솟값', apply: 'MIN(source.)', info: 'MIN(expr) — 최솟값' },
  { label: 'MAX', type: 'function', detail: '최댓값', apply: 'MAX(source.)', info: 'MAX(expr) — 최댓값' },
  { label: 'MEASURE', type: 'function', detail: '복합 지표 참조', apply: 'MEASURE()', info: 'MEASURE(other_measure) — 다른 measure 참조' },
  { label: 'COUNTIF', type: 'function', detail: '조건부 카운트', apply: 'COUNTIF()', info: 'COUNTIF(condition) — 조건 만족 행 수' },
  { label: 'APPROX_COUNT_DISTINCT', type: 'function', detail: '근사 고유 카운트', apply: 'APPROX_COUNT_DISTINCT(source.)', info: '대규모 데이터 근사 고유 카운트' },
]

// 5. 스칼라/날짜 함수 (dimension expr용)
const SCALAR_FUNCTIONS = [
  { label: 'DATE', type: 'function', detail: '날짜 변환', apply: 'DATE(source.)', info: 'DATE(timestamp) → DATE' },
  { label: 'DATE_TRUNC', type: 'function', detail: '날짜 절삭', apply: "DATE_TRUNC('month', source.)", info: "DATE_TRUNC('month'|'quarter'|'year', date)" },
  { label: 'TIMESTAMP', type: 'function', detail: '타임스탬프 변환', apply: 'TIMESTAMP(source.)', info: 'TIMESTAMP(expr)' },
  { label: 'CAST', type: 'function', detail: '타입 변환', apply: 'CAST(source. AS )', info: 'CAST(expr AS type)' },
  { label: 'COALESCE', type: 'function', detail: 'NULL 대체', apply: 'COALESCE(source., )', info: 'COALESCE(expr, default) — 첫 번째 non-NULL' },
  { label: 'IFNULL', type: 'function', detail: 'NULL이면 대체', apply: 'IFNULL(source., )', info: 'IFNULL(expr, default)' },
  { label: 'NULLIF', type: 'function', detail: '같으면 NULL', apply: 'NULLIF(source., )', info: 'NULLIF(a, b) — a == b이면 NULL' },
  { label: 'IF', type: 'function', detail: '조건 분기', apply: 'IF(, , )', info: 'IF(condition, true_val, false_val)' },
  { label: 'CASE', type: 'function', detail: '조건 분기 (복수)', apply: "CASE WHEN  THEN  ELSE  END", info: 'CASE WHEN ... THEN ... END' },
  { label: 'CONCAT', type: 'function', detail: '문자열 연결', apply: 'CONCAT(source., source.)', info: 'CONCAT(str1, str2, ...)' },
  { label: 'SUBSTR', type: 'function', detail: '부분 문자열', apply: 'SUBSTR(source., 1, )', info: 'SUBSTR(str, start, length)' },
  { label: 'UPPER', type: 'function', detail: '대문자 변환', apply: 'UPPER(source.)', info: 'UPPER(str)' },
  { label: 'LOWER', type: 'function', detail: '소문자 변환', apply: 'LOWER(source.)', info: 'LOWER(str)' },
  { label: 'TRIM', type: 'function', detail: '공백 제거', apply: 'TRIM(source.)', info: 'TRIM(str)' },
  { label: 'LENGTH', type: 'function', detail: '문자열 길이', apply: 'LENGTH(source.)', info: 'LENGTH(str)' },
  { label: 'ROUND', type: 'function', detail: '반올림', apply: 'ROUND(source., 2)', info: 'ROUND(num, digits)' },
  { label: 'ABS', type: 'function', detail: '절대값', apply: 'ABS(source.)', info: 'ABS(num)' },
  { label: 'FLOOR', type: 'function', detail: '내림', apply: 'FLOOR(source.)', info: 'FLOOR(num)' },
  { label: 'CEIL', type: 'function', detail: '올림', apply: 'CEIL(source.)', info: 'CEIL(num)' },
  { label: 'EXTRACT', type: 'function', detail: '날짜 부분 추출', apply: 'EXTRACT( FROM source.)', info: 'EXTRACT(YEAR|MONTH|DAY FROM date)' },
  { label: 'FORMAT_DATE', type: 'function', detail: '날짜 포맷', apply: "FORMAT_DATE('%Y-%m-%d', source.)", info: "FORMAT_DATE(format, date)" },
  { label: 'DATE_DIFF', type: 'function', detail: '날짜 차이', apply: 'DATE_DIFF(source., source., DAY)', info: 'DATE_DIFF(date1, date2, part)' },
  { label: 'DATE_ADD', type: 'function', detail: '날짜 더하기', apply: 'DATE_ADD(source., INTERVAL 1 DAY)', info: 'DATE_ADD(date, INTERVAL n part)' },
  { label: 'SAFE_DIVIDE', type: 'function', detail: '안전 나눗셈', apply: 'SAFE_DIVIDE(, )', info: 'SAFE_DIVIDE(a, b) — 0 나눗셈 시 NULL' },
]

// 6. 값 자동완성
const VERSION_VALUES = [
  { label: "'1.1'", type: 'constant', detail: '현재 버전', apply: "'1.1'" },
]

const JOIN_TYPE_VALUES = [
  { label: 'LEFT', type: 'constant', detail: 'LEFT OUTER JOIN' },
  { label: 'INNER', type: 'constant', detail: 'INNER JOIN' },
  { label: 'CROSS', type: 'constant', detail: 'CROSS JOIN' },
  { label: 'FULL', type: 'constant', detail: 'FULL OUTER JOIN' },
]

const DATE_TRUNC_PARTS = [
  { label: "'day'", type: 'constant', detail: '일 단위', apply: "'day'" },
  { label: "'week'", type: 'constant', detail: '주 단위', apply: "'week'" },
  { label: "'month'", type: 'constant', detail: '월 단위', apply: "'month'" },
  { label: "'quarter'", type: 'constant', detail: '분기 단위', apply: "'quarter'" },
  { label: "'year'", type: 'constant', detail: '연 단위', apply: "'year'" },
]

// 7. 스니펫 (블록 템플릿)
const SNIPPETS = [
  {
    label: '# new dimension',
    type: 'text',
    detail: '디멘전 블록 추가',
    boost: -1,
    apply: `- name: \n    expr: source.\n    display_name: ""\n    comment: ""\n    synonyms: []\n    sql_hint: ""`,
    info: 'dimension 항목을 추가합니다.',
  },
  {
    label: '# new measure',
    type: 'text',
    detail: '메저 블록 추가',
    boost: -1,
    apply: `- name: \n    expr: SUM(source.)\n    display_name: ""\n    comment: ""\n    synonyms: []\n    sql_hint: ""`,
    info: 'measure 항목을 추가합니다.',
  },
  {
    label: '# new join',
    type: 'text',
    detail: '조인 블록 추가',
    boost: -1,
    apply: `- name: \n    source: \n    type: LEFT\n    'on': ""`,
    info: 'join 항목을 추가합니다.',
  },
  {
    label: '# date dimension pair',
    type: 'text',
    detail: '날짜 디멘전 쌍 (원본 + month)',
    boost: -2,
    apply: `- name: _date\n    expr: DATE(source.)\n    display_name: ""\n    comment: ""\n    synonyms: []\n    sql_hint: ""\n  - name: _month\n    expr: DATE_TRUNC('month', source.)\n    display_name: ""\n    comment: "월별 집계용"\n    synonyms: []\n    sql_hint: ""`,
    info: '날짜 디멘전 + 월 디멘전 쌍을 추가합니다.',
  },
  {
    label: '# code-to-name CASE',
    type: 'text',
    detail: '코드값→이름 CASE 변환',
    boost: -2,
    apply: `CASE source.\n      WHEN '' THEN ''\n      WHEN '' THEN ''\n      ELSE source.\n    END`,
    info: '코드값을 비즈니스 이름으로 변환하는 CASE 문',
  },
  {
    label: '# composite measure',
    type: 'text',
    detail: '복합 지표 (measure 참조)',
    boost: -2,
    apply: `- name: \n    expr: SAFE_DIVIDE(MEASURE(), MEASURE())\n    display_name: ""\n    comment: ""\n    synonyms: []\n    sql_hint: ""`,
    info: 'MEASURE() 참조를 사용한 복합 지표',
  },
]

// ── 컨텍스트 분석 ───────────────────────────────────────────────────────────

/**
 * 현재 커서 위치의 컨텍스트를 분석합니다.
 * @returns {{ section, key, afterColon, indent, lineText }}
 */
function analyzeContext(state, pos) {
  const doc = state.doc
  const line = doc.lineAt(pos)
  const lineText = line.text
  const textBefore = lineText.slice(0, pos - line.from)

  // 들여쓰기 레벨
  const indent = textBefore.length - textBefore.trimStart().length

  // 콜론 뒤인지 (값 위치)
  const colonIdx = textBefore.indexOf(':')
  const afterColon = colonIdx >= 0 && pos - line.from > colonIdx + 1

  // 콜론 앞의 키
  const key = afterColon ? textBefore.slice(0, colonIdx).trim().replace(/^-\s*/, '') : ''

  // 현재 섹션 탐색 (위로 올라가며 dimensions/measures/joins 찾기)
  let section = 'root'
  for (let lineNum = line.number - 1; lineNum >= 1; lineNum--) {
    const prevLine = doc.line(lineNum).text
    const trimmed = prevLine.trimStart()
    const prevIndent = prevLine.length - trimmed.length

    if (prevIndent === 0) {
      if (trimmed.startsWith('dimensions')) section = 'dimensions'
      else if (trimmed.startsWith('measures')) section = 'measures'
      else if (trimmed.startsWith('joins')) section = 'joins'
      else if (trimmed.startsWith('version')) section = 'root'
      else if (trimmed.startsWith('source')) section = 'root'
      else if (trimmed.startsWith('filter')) section = 'root'
      break
    }
  }

  return { section, key, afterColon, indent, lineText, textBefore }
}

// ── 자동완성 소스 ───────────────────────────────────────────────────────────

function semanticCompletionSource(context) {
  // 최소 1글자부터 트리거 (또는 명시적 호출)
  const word = context.matchBefore(/[\w'#.]+/)
  if (!word && !context.explicit) return null

  const from = word ? word.from : context.pos
  const text = word ? word.text : ''

  const { section, key, afterColon, indent } = analyzeContext(context.state, context.pos)

  let options = []

  if (afterColon) {
    // 값 위치: 키에 따라 다른 완성 제공
    if (key === 'version') {
      options = VERSION_VALUES
    } else if (key === 'type' && section === 'joins') {
      options = JOIN_TYPE_VALUES
    } else if (key === 'expr' && section === 'measures') {
      options = [...AGG_FUNCTIONS, ...SCALAR_FUNCTIONS]
    } else if (key === 'expr' && section === 'dimensions') {
      options = [...SCALAR_FUNCTIONS]
    } else if (key === 'expr') {
      options = [...AGG_FUNCTIONS, ...SCALAR_FUNCTIONS]
    } else if (key === 'synonyms') {
      // 빈 배열 또는 값은 직접 입력
      return null
    }
  } else if (indent === 0) {
    // 루트 레벨
    options = ROOT_KEYS
  } else if (indent >= 2) {
    // 블록 내부
    const trimmed = text.replace(/^-\s*/, '')

    if (section === 'joins') {
      options = [...JOIN_PROPS, ...SNIPPETS.filter(s => s.label === '# new join')]
    } else if (section === 'dimensions') {
      options = [...DIM_MEAS_PROPS, ...SNIPPETS.filter(s =>
        s.label.includes('dimension') || s.label.includes('date') || s.label.includes('CASE')
      )]
    } else if (section === 'measures') {
      options = [...DIM_MEAS_PROPS, ...SNIPPETS.filter(s =>
        s.label.includes('measure') || s.label.includes('composite')
      )]
    } else {
      options = [...DIM_MEAS_PROPS, ...SNIPPETS]
    }

    // # 으로 시작하면 스니펫 우선
    if (trimmed.startsWith('#')) {
      options = options.filter(o => o.label.startsWith('#'))
    }
  }

  if (options.length === 0) return null

  return {
    from,
    options,
    validFor: /^[\w'#.\-]*$/,
  }
}

// DATE_TRUNC의 첫 번째 인자 위치 감지
function dateTruncCompletionSource(context) {
  const before = context.matchBefore(/DATE_TRUNC\(\s*['"]?\w*/)
  if (!before) return null

  const parenIdx = before.text.indexOf('(')
  if (parenIdx < 0) return null

  const afterParen = before.text.slice(parenIdx + 1).trimStart()
  // 쉼표가 없으면 첫 번째 인자 (part)
  if (!before.text.includes(',')) {
    const quoteMatch = afterParen.match(/^['"]?(\w*)$/)
    if (quoteMatch) {
      return {
        from: before.from + parenIdx + 1 + (before.text.slice(parenIdx + 1).length - before.text.slice(parenIdx + 1).trimStart().length),
        options: DATE_TRUNC_PARTS,
        validFor: /^['"]?\w*$/,
      }
    }
  }
  return null
}

// ── Extension 생성 ──────────────────────────────────────────────────────────

/**
 * Semantic YAML 자동완성 extension을 생성합니다.
 *
 * 사용법:
 *   import { semanticAutocomplete } from './autocomplete-semantic'
 *   extensions={[yaml(), semanticAutocomplete()]}
 */
export function semanticAutocomplete() {
  return autocompletion({
    override: [
      semanticCompletionSource,
      dateTruncCompletionSource,
    ],
    activateOnTyping: true,
    maxRenderedOptions: 30,
    icons: true,
    addToOptions: [
      // info를 툴팁으로 표시
      {
        render(completion) {
          if (!completion.info) return null
          const el = document.createElement('span')
          el.className = 'cm-completionInfo'
          el.textContent = completion.info
          return el
        },
        position: 80,
      }
    ],
  })
}
